import type { ManagementPosition } from '@/types/site';
import * as yup from 'yup';

// Position validation schema
const positionSchema = yup.object({
  latitude: yup.number().required('위도는 필수 항목입니다'),
  longitude: yup.number().required('경도는 필수 항목입니다'),
  address: yup.string().required('주소는 필수 항목입니다'),
  level: yup.number().default(0),
  name: yup.string().required('이름은 필수 항목입니다'),
});

// Management position validation schema (all fields optional)
const managementPositionSchema: yup.ObjectSchema<ManagementPosition> =
  yup.object({
    latitude: yup.number().optional(),
    longitude: yup.number().optional(),
    address: yup.string().optional().default(''),
    level: yup.number().optional().default(0),
    name: yup.string().optional().default(''),
  });

// Water supply data validation schema
const waterSupplyDataSchema = yup.object({
  family: yup.number().min(0, '가구 수는 0 이상이어야 합니다').default(0),
  people: yup.number().min(0, '인구 수는 0 이상이어야 합니다').default(0),
  waterSupplyPerPerson: yup
    .number()
    .min(0, '1인당 급수량은 0 이상이어야 합니다')
    .default(0),
});

// Water supply info validation schema
const waterSupplyInfoSchema = yup.object({
  plan: waterSupplyDataSchema,
  current: waterSupplyDataSchema,
  supplyLimit: yup.string().default(''),
});

// Facility info validation schema
const facilityInfoSchema = yup.object({
  type: yup.string().default(''),
  specification: yup.string().default(''),
  state: yup.string().default(''),
  installedDate: yup.string().default(''),
  explanatoryNote: yup.string().default(''),
});

// Pipeline info validation schema
const pipelineInfoSchema = yup.object({
  type: yup.string().default(''),
  diameter: yup.number().min(0, '직경은 0 이상이어야 합니다').default(0),
  extension: yup.number().min(0, '연장은 0 이상이어야 합니다').default(0),
});

// Facility plan validation schema
const facilityPlanSchema = yup.object({
  repairDate: yup.string().default(''),
  closeDate: yup.string().default(''),
  supplyDate: yup.string().default(''),
  distanceFromWaterSupply: yup
    .number()
    .min(0, '급수지와의 거리는 0 이상이어야 합니다')
    .default(0),
  explanatoryNote: yup.string().default(''),
});

// Water source validation schema
const waterSourceSchema = yup.object({
  date: yup.string().default(''),
  reason: yup.string().default(''),
  cost: yup.string().default(''),
  address: yup.string().default(''),
});

// Investment info validation schema
const investmentInfoSchema = yup.object({
  type: yup.string().default(''),
  contractor: yup.string().default(''),
  constructDate: yup.string().default(''),
  countryCost: yup.number().min(0, '국비는 0 이상이어야 합니다').default(0),
  provinceCost: yup.number().min(0, '도비는 0 이상이어야 합니다').default(0),
  cityCost: yup.number().min(0, '시비는 0 이상이어야 합니다').default(0),
  citizenCost: yup
    .number()
    .min(0, '군민부담금은 0 이상이어야 합니다')
    .default(0),
  etcCost: yup.number().min(0, '기타 비용은 0 이상이어야 합니다').default(0),
  explanatoryNote: yup.string().default(''),
});

// Main site validation schema
export const siteValidationSchema = yup.object({
  // 기본 정보 (필수)
  siteId: yup
    .string()
    .required('사이트 ID는 필수 항목입니다')
    .min(6, '사이트 ID 6자리를 입력해주세요')
    .max(6, '사이트 ID 6자리를 입력해주세요'),

  name: yup
    .string()
    .required('현장명은 필수 항목입니다')
    .min(1, '현장명을 입력해주세요'),
  cdmaNumber: yup
    .string()
    .default('')
    .required('CDMA 번호는 필수 항목입니다')
    .min(11, '11자리 CDMA 번호를 입력해주세요')
    .max(11, '11자리 CDMA 번호를 입력해주세요'),

  // 모터 활성화 개수 (필수)
  motorActivateCount: yup
    .number()
    .min(1, '모터 활성화 개수는 1 이상이어야 합니다')
    .max(3, '모터 활성화 개수는 3개 이하여야 합니다')
    .default(1)
    .required('모터 활성화 개수는 필수 항목입니다'),

  // 별칭 (선택)
  alias: yup
    .object({
      circuitBreaker: yup.string().optional().default('차단기'),
      pressureSensor: yup.string().optional().default('압력센서'),
      external: yup.string().optional().default('외부'),
      preparation: yup.string().optional().default('예비1'),
      preparation2: yup.string().optional().default('예비2'),
    })
    .default({
      circuitBreaker: '차단기',
      pressureSensor: '압력센서',
      external: '외부',
      preparation: '예비1',
      preparation2: '예비2',
    }),

  // 관리번호 (선택)
  managementNumber: yup.string().default(''),

  // 위치 정보 (필수)
  wellPosition: positionSchema.required('정수지 위치는 필수 항목입니다'),
  waterSupplyPosition: yup.array().of(positionSchema).default([]).optional(),
  managementPosition: managementPositionSchema.optional(),

  // 분류 정보 (선택)
  managementType: yup.string().default(''),
  facilityType: yup.string().default(''),
  waterIntakeType: yup.string().default(''),

  // 시설 정보 (선택)
  installedDate: yup.string().default(''),
  motorSpecification: yup.string().default(''),
  tankSize: yup.number().min(0, '탱크 용량은 0 이상이어야 합니다').default(0),

  // 급수 정보 (선택)
  waterSupplyInfo: waterSupplyInfoSchema.default({
    plan: { family: 0, people: 0, waterSupplyPerPerson: 0 },
    current: { family: 0, people: 0, waterSupplyPerPerson: 0 },
    supplyLimit: '',
  }),

  // 시설 정보 배열 (선택)
  intakeFacilityInfo: yup.array().of(facilityInfoSchema).default([]),
  purifyFacilityInfo: yup.array().of(facilityInfoSchema).default([]),

  // 배관 정보 배열 (선택)
  waterSupplyPipelineInfo: yup.array().of(pipelineInfoSchema).default([]),
  drainageSupplyPipelineInfo: yup.array().of(pipelineInfoSchema).default([]),

  // 시설 계획 (선택)
  facilityPlan: facilityPlanSchema.default({
    repairDate: '',
    closeDate: '',
    supplyDate: '',
    distanceFromWaterSupply: 0,
    explanatoryNote: '',
  }),

  // 수원 정보 (선택)
  waterSource: yup.array().of(waterSourceSchema).default([]),

  // 투자 정보 (선택)
  investmentInfos: yup.array().of(investmentInfoSchema).default([]),

  // 알림 설정 (선택)
  lowWaterLevel: yup
    .number()
    .min(0, '저수위는 0 이상이어야 합니다')
    .max(100, '저수위는 100 이하여야 합니다')
    .default(40),
  highWaterLevel: yup
    .number()
    .min(0, '고수위는 0 이상이어야 합니다')
    .max(100, '고수위는 100 이하여야 합니다')
    .default(100),
  notificationManagerIds: yup.array().of(yup.string().required()).default([]),

  // 에러 상황별 SMS 알림 설정 (선택)
  electricWaveErrorSmsEnabled: yup.boolean().default(false),
  overloadErrorSmsEnabled: yup.boolean().default(false),
  undergroundWaterErrorSmsEnabled: yup.boolean().default(false),
  waterLevelSmsEnabled: yup.boolean().default(true),
});

// 기본 validation 에러 메시지 설정
yup.setLocale({
  mixed: {
    required: '${label}은(는) 필수 항목입니다',
  },
  string: {
    min: '${label}은(는) 최소 ${min}자 이상이어야 합니다',
  },
  number: {
    min: '${label}은(는) ${min} 이상이어야 합니다',
  },
});
